/**
 * ALTIORA · Avatar Onboarding — clone physique et vocal.
 *
 * Guide l'utilisateur pas à pas :
 * 1. Vérifie l'état des providers (HeyGen + ElevenLabs)
 * 2. Enregistrement de sa vidéo/audio source (instructions détaillées)
 * 3. Upload vers HeyGen/ElevenLabs (liens directs)
 * 4. Test du clone avec un script court → vidéo et audio générés
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Video, Mic, CheckCircle2, XCircle, PlayCircle,
  UploadCloud, Sparkles, Info, ExternalLink,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, PrimaryButton, GhostButton, FormField,
  Input, Alert, Loader, NAVY, CHAMPAGNE, SLATE, NAVY_DARK as INK,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

const STEPS = [
  { id: 1, title: "Vérifier les providers",   icon: Info },
  { id: 2, title: "Enregistrer les sources",   icon: Video },
  { id: 3, title: "Uploader chez les providers", icon: UploadCloud },
  { id: 4, title: "Tester le clone",           icon: PlayCircle },
];

export default function AdminAvatarOnboarding() {
  const [config, setConfig] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [voiceRes, setVoiceRes] = useState(null);
  const [videoRes, setVideoRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadConfig = () => Promise.all([
    api.get("/growth/avatar/config"),
    api.get("/growth/avatar/my-profile"),
  ]).then(([c, m]) => { setConfig(c.data); setMyProfile(m.data); });

  useEffect(() => { loadConfig(); }, []);

  const testVoice = async () => {
    setBusy(true); setVoiceRes(null);
    try {
      const { data } = await api.post("/growth/avatar/voice-test",
                                          null, { params: { text:
        "Bonjour, je teste ma voix clonée pour ALTIORA. Aujourd hui je vais vous parler d une nouveauté REEE." } });
      setVoiceRes(data);
      toast[data.status === "ready" ? "success" : "error"](
        data.status === "ready" ? "Audio généré." : data.error);
    } catch (e) { toast.error("Échec test voix"); }
    finally { setBusy(false); }
  };

  const testVideo = async () => {
    setBusy(true); setVideoRes(null);
    try {
      const { data } = await api.post("/growth/avatar/test", {
        script: "Bonjour, je suis le clone virtuel d'ALTIORA. Je peux transformer vos idées en vidéos LinkedIn en quelques secondes.",
      });
      setVideoRes(data);
      toast[data.status === "ready" || data.status === "rendering" ? "success" : "error"](
        data.status !== "failed"
          ? `Rendu ${data.status} — provider ${data.provider}`
          : data.error);
      // Auto-refresh toutes les 20s tant que status = rendering
      if (data.status === "rendering" && data.id) {
        pollVideoStatus(data.id);
      }
    } catch (e) { toast.error("Échec test vidéo"); }
    finally { setBusy(false); }
  };

  const pollVideoStatus = async (jobId, attempts = 0) => {
    if (attempts > 12) return;   // stop après ~4 min
    setTimeout(async () => {
      try {
        const { data } = await api.post(`/growth/avatar/jobs/${jobId}/refresh`);
        setVideoRes(data);
        if (data.status === "rendering") pollVideoStatus(jobId, attempts + 1);
        else if (data.status === "ready") toast.success("Vidéo prête ! 🎬");
      } catch {}
    }, 20000);
  };

  if (!config) return <PageShell><TopBar /><Loader /></PageShell>;

  const hgOk = config.heygen?.api_key_present && config.heygen?.avatar_id;
  const elOk = config.elevenlabs?.api_key_present && config.elevenlabs?.voice_id;
  const mineHgOk = myProfile?.heygen_api_key_present && myProfile?.heygen_avatar_id;
  const mineElOk = myProfile?.elevenlabs_api_key_present && myProfile?.elevenlabs_voice_id;

  return (
    <PageShell>
      <TopBar />
      <div className="max-w-[1200px] mx-auto px-6 py-8"
            data-testid="admin-avatar-onboarding-root">
        <PageHeader
          eyebrow="ALTIORA · Clone physique et vocal"
          title="Avatar Onboarding"
          subtitle="Créez votre double numérique — votre visage, votre voix, votre présence — en quelques minutes."
          testid="avatar-onboarding-header"
        />

        {/* Provider status cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <ProviderStatusCard title="HeyGen · Clone physique" icon={Video}
                                  active={hgOk}
                                  api_key={config.heygen.api_key_present}
                                  id_val={config.heygen.avatar_id}
                                  id_label="AVATAR_ID"
                                  url="https://www.heygen.com" />
          <ProviderStatusCard title="ElevenLabs · Clone vocal" icon={Mic}
                                  active={elOk}
                                  api_key={config.elevenlabs.api_key_present}
                                  id_val={config.elevenlabs.voice_id}
                                  id_label="VOICE_ID"
                                  url="https://elevenlabs.io" />
        </div>

        {/* Step 2 — Recording instructions */}
        <Card className="mt-6" testid="onboarding-step-record">
          <h3 className="font-semibold text-lg mb-3" style={{ color: INK }}>
            <span className="inline-block px-2 mr-2 rounded-full text-xs"
                    style={{ background: CHAMPAGNE, color: NAVY }}>1</span>
            Enregistrer vos sources
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <RecordingInstructions kind="video" />
            <RecordingInstructions kind="audio" />
          </div>
        </Card>

        {/* Step 3 — Upload docs */}
        <Card className="mt-4" testid="onboarding-step-upload">
          <h3 className="font-semibold text-lg mb-3" style={{ color: INK }}>
            <span className="inline-block px-2 mr-2 rounded-full text-xs"
                    style={{ background: CHAMPAGNE, color: NAVY }}>2</span>
            Uploader chez les providers
          </h3>
          <ol className="text-sm space-y-2 mt-2" style={{ color: INK }}>
            <li>
              <strong>HeyGen</strong> : allez sur{" "}
              <a href="https://app.heygen.com/avatars" target="_blank"
                  rel="noreferrer"
                  className="underline" style={{ color: NAVY }}>
                app.heygen.com/avatars <ExternalLink size={12}
                                                        className="inline" />
              </a>{" "}
              → « Create Instant Avatar » → uploadez votre vidéo 2 min →
              copiez l'<code>avatar_id</code> généré.
            </li>
            <li>
              <strong>ElevenLabs</strong> : allez sur{" "}
              <a href="https://elevenlabs.io/app/voice-lab" target="_blank"
                  rel="noreferrer"
                  className="underline" style={{ color: NAVY }}>
                elevenlabs.io/app/voice-lab <ExternalLink size={12}
                                                            className="inline" />
              </a>{" "}
              → « Add Voice » → « Instant Voice Cloning » → uploadez votre
              audio 1-2 min → copiez le <code>voice_id</code> généré.
            </li>
            <li>
              Placez les 4 variables suivantes dans <code>/app/backend/.env</code> :
              <pre className="mt-2 p-3 rounded text-xs overflow-x-auto"
                    style={{ background: "#0a2540", color: CHAMPAGNE }}>
{`HEYGEN_API_KEY=hg_xxxxxxxxxxxx
HEYGEN_AVATAR_ID=avatar_xxxxxxxxx
ELEVENLABS_API_KEY=xi_xxxxxxxxxxxx
ELEVENLABS_VOICE_ID=voice_xxxxxxxxx`}
              </pre>
            </li>
            <li>Redémarrez le backend :
              <code className="ml-1 px-2 py-1 rounded"
                    style={{ background: "#0a254011" }}>
                sudo supervisorctl restart backend
              </code>
            </li>
          </ol>
          <div className="mt-4 flex gap-2">
            <a href="/app/docs/HEYGEN_SETUP.md" target="_blank"
                data-testid="link-heygen-doc"
                className="text-sm underline" style={{ color: NAVY }}>
              Guide détaillé HeyGen ↗
            </a>
            <a href="/app/docs/ELEVENLABS_SETUP.md" target="_blank"
                data-testid="link-elevenlabs-doc"
                className="text-sm underline" style={{ color: NAVY }}>
              Guide détaillé ElevenLabs ↗
            </a>
          </div>
        </Card>

        {/* Step 3 · Personal Profile (multi-tenant) */}
        <MyAvatarProfileSection myProfile={myProfile} onSaved={loadConfig} />

        {/* Step 3.5 · Sélection avatar HeyGen depuis la galerie */}
        <HeyGenAvatarGallery myProfile={myProfile} onSelected={loadConfig} />

        {/* Step 4 — Test */}
        <Card className="mt-4" testid="onboarding-step-test">
          <h3 className="font-semibold text-lg mb-3" style={{ color: INK }}>
            <span className="inline-block px-2 mr-2 rounded-full text-xs"
                    style={{ background: CHAMPAGNE, color: NAVY }}>4</span>
            Tester le clone
          </h3>
          <div className="flex flex-wrap gap-3">
            <PrimaryButton onClick={testVoice} disabled={busy}
                              data-testid="btn-test-voice">
              {busy ? <Loader /> : <><Mic size={14} /> Test voix seule</>}
            </PrimaryButton>
            <PrimaryButton onClick={testVideo} disabled={busy}
                              data-testid="btn-test-video">
              {busy ? <Loader /> : <><Video size={14} /> Test vidéo complète</>}
            </PrimaryButton>
            <GhostButton onClick={loadConfig} data-testid="btn-refresh-config">
              Rafraîchir config
            </GhostButton>
          </div>

          {voiceRes && (
            <div className="mt-4 p-3 rounded" data-testid="voice-result"
                  style={{ background: voiceRes.status === "ready"
                             ? "#0a7f4211" : "#c9505011" }}>
              <div className="text-xs uppercase" style={{ color: SLATE }}>
                Résultat voix — {voiceRes.provider}
              </div>
              <div className="text-sm mt-1" style={{ color: INK }}>
                Statut : <strong>{voiceRes.status}</strong>
                {voiceRes.error && <span> · {voiceRes.error}</span>}
              </div>
              {voiceRes.audio_base64 && (
                <audio controls className="mt-2 w-full"
                        src={`data:audio/mpeg;base64,${voiceRes.audio_base64}`} />
              )}
              {voiceRes.audio_url && !voiceRes.audio_base64 && (
                <audio controls className="mt-2 w-full" src={voiceRes.audio_url} />
              )}
            </div>
          )}

          {videoRes && (
            <div className="mt-4 p-3 rounded" data-testid="video-result"
                  style={{ background: videoRes.status !== "failed"
                             ? "#0a7f4211" : "#c9505011" }}>
              <div className="text-xs uppercase" style={{ color: SLATE }}>
                Résultat vidéo — {videoRes.provider}
              </div>
              <div className="text-sm mt-1" style={{ color: INK }}>
                Statut : <strong>{videoRes.status}</strong>
                {videoRes.error && <span> · {videoRes.error}</span>}
              </div>
              {videoRes.status === "rendering" && (
                <p className="text-xs mt-2" style={{ color: SLATE }}>
                  HeyGen traite votre vidéo — cela prend habituellement
                  1 à 3 minutes. Vous pouvez fermer la page, le rendu se
                  poursuit en arrière-plan. Consultez l'historique dans
                  Growth & Media Engine → Avatar.
                </p>
              )}
              {videoRes.video_url && (
                <>
                  <video controls className="mt-2 w-full rounded max-h-96"
                          src={videoRes.video_url}
                          data-testid="video-player" />
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <DownloadVideoButton jobId={videoRes.id} />
                    <a href={videoRes.video_url} target="_blank" rel="noreferrer"
                        data-testid="btn-open-tab"
                        className="text-xs underline"
                        style={{ color: NAVY }}>
                      Ouvrir dans un nouvel onglet ↗
                    </a>
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    </PageShell>
  );
}

function DownloadVideoButton({ jobId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.get(`/growth/avatar/jobs/${jobId}/download`,
                                     { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `altiora_avatar_${jobId}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Téléchargement démarré");
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 409
        ? "Vidéo pas encore prête — patientez."
        : "Échec du téléchargement");
    } finally { setBusy(false); }
  };
  return (
    <PrimaryButton onClick={download} disabled={busy}
                      data-testid="btn-download-video">
      {busy ? <Loader /> : <><UploadCloud size={12}
                                              className="rotate-180" /> Télécharger MP4</>}
    </PrimaryButton>
  );
}


function ProviderStatusCard({ title, icon: Icon, active, api_key,
                                 id_val, id_label, url }) {
  return (
    <Card testid={`provider-${title.toLowerCase().split(" ")[0]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={18} style={{ color: CHAMPAGNE }} />
          <h4 className="font-semibold" style={{ color: INK }}>{title}</h4>
        </div>
        {active ? (
          <span className="flex items-center gap-1 text-xs"
                  style={{ color: "#0a7f42" }}>
            <CheckCircle2 size={14} /> Actif
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs"
                  style={{ color: "#c95050" }}>
            <XCircle size={14} /> Inactif
          </span>
        )}
      </div>
      <div className="text-xs mt-2" style={{ color: SLATE }}>
        <div>Clé API : {api_key ? "✔ présente" : "✘ absente"}</div>
        <div>{id_label} : <code>{id_val || "—"}</code></div>
      </div>
      <a href={url} target="_blank" rel="noreferrer"
          className="text-xs underline mt-2 inline-block"
          style={{ color: NAVY }}>
        Ouvrir la console {title.split(" · ")[0]} <ExternalLink size={10}
                                                                    className="inline" />
      </a>
    </Card>
  );
}

function HeyGenAvatarGallery({ myProfile, onSelected }) {
  const [q, setQ] = useState("professional dark business suit");
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/growth/avatar/heygen/list");
      setAvatars(data.avatars || []);
      setLoaded(true);
      toast.success(`${data.total} avatars HeyGen chargés`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lister les avatars HeyGen");
    } finally { setLoading(false); }
  };

  const select = async (avatarId, name) => {
    setSelecting(avatarId);
    try {
      await api.post("/growth/avatar/heygen/select", { avatar_id: avatarId });
      toast.success(`Avatar sélectionné : ${name}`);
      onSelected();
      // Marquer localement pour un feedback instant
      setAvatars(prev => prev.map(a => ({ ...a, is_current: a.id === avatarId })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec sélection");
    } finally { setSelecting(null); }
  };

  const filtered = q.trim()
    ? (() => {
        // Recherche multi-mots : chaque mot doit être présent dans le nom OU l'id
        const words = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
        return avatars.filter(a => {
          const hay = ((a.name || "") + " " + (a.id || "")).toLowerCase();
          return words.every(w => hay.includes(w));
        }).slice(0, 60);
      })()
    : avatars.slice(0, 60);

  return (
    <Card className="mt-4" testid="heygen-gallery">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg" style={{ color: INK }}>
          <span className="inline-block px-2 mr-2 rounded-full text-xs"
                  style={{ background: CHAMPAGNE, color: NAVY }}>3b</span>
          Galerie avatars HeyGen
        </h3>
        {!loaded && (
          <PrimaryButton onClick={load} disabled={loading}
                            data-testid="btn-load-avatars">
            {loading ? <Loader /> : "Charger les avatars"}
          </PrimaryButton>
        )}
      </div>
      <p className="text-sm mb-3" style={{ color: SLATE }}>
        Parcourez les avatars publics HeyGen (8000+) et cliquez pour sélectionner
        celui qui deviendra votre clone. L'ID sélectionné est enregistré dans
        votre profil chiffré et utilisé pour toutes vos futures vidéos.
      </p>

      {loaded && (
        <>
          <div className="mb-3 flex gap-2 items-center">
            <input value={q} onChange={e => setQ(e.target.value)}
                    data-testid="avatar-gallery-search"
                    placeholder="Recherche : professional, business suit, blazer…"
                    className="flex-1 px-3 py-2 border rounded text-sm"
                    style={{ borderColor: "#0a254033" }} />
            <span className="text-xs" style={{ color: SLATE }}>
              {filtered.length} / {avatars.length} affichés
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[520px] overflow-y-auto pr-1"
                data-testid="avatar-gallery-grid">
            {filtered.map(a => (
              <div key={a.id}
                    className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${a.is_current ? "ring-2" : ""}`}
                    style={{ borderColor: a.is_current ? "#c9a34e" : "#0a254022",
                              boxShadow: a.is_current ? "0 0 0 2px #c9a34e55" : "none" }}
                    data-testid={`avatar-card-${a.id}`}
                    onClick={() => !selecting && select(a.id, a.name)}>
                <div className="aspect-[3/4] bg-slate-100 relative">
                  {a.preview_image_url ? (
                    <img src={a.preview_image_url} alt={a.name}
                         className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs"
                          style={{ color: SLATE }}>Pas de preview</div>
                  )}
                  {a.is_current && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ background: CHAMPAGNE, color: NAVY }}>
                      ✔ Actuel
                    </div>
                  )}
                  {selecting === a.id && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xs">
                      Sélection…
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold line-clamp-2" style={{ color: INK }}>
                    {a.name}
                  </div>
                  <div className="text-xs" style={{ color: SLATE }}>
                    {a.gender || a.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center text-sm py-8" style={{ color: SLATE }}>
              Aucun avatar ne correspond à « {q} »
            </div>
          )}
        </>
      )}
    </Card>
  );
}


function RecordingInstructions({ kind }) {
  const isVideo = kind === "video";
  return (
    <div className="p-4 rounded" style={{ background: "#F7F1E1" }}
          data-testid={`instructions-${kind}`}>
      <div className="flex items-center gap-2 mb-2">
        {isVideo ? <Video size={16} style={{ color: NAVY }} />
                  : <Mic size={16} style={{ color: NAVY }} />}
        <h4 className="font-semibold" style={{ color: NAVY }}>
          {isVideo ? "Vidéo (visage)" : "Audio (voix)"}
        </h4>
      </div>
      <ul className="text-sm space-y-1" style={{ color: INK }}>
        {isVideo ? (
          <>
            <li>• MP4 1080p, face caméra, cadre poitrine</li>
            <li>• 2 minutes exactement (HeyGen exige ≥ 2 min)</li>
            <li>• Éclairage naturel de face (fenêtre)</li>
            <li>• Vous parlez normalement, souriez, bougez la tête</li>
            <li>• Fond neutre uni de préférence</li>
            <li>• Vous pouvez lire ce texte : « Bonjour, je suis conseiller en sécurité financière au Québec. J'aide les familles à protéger leur avenir et à structurer leur patrimoine. Chaque décision compte… » </li>
          </>
        ) : (
          <>
            <li>• WAV ou MP3 haute qualité (44100 Hz)</li>
            <li>• 1 à 2 minutes (ElevenLabs Instant : 1-2 min suffisent)</li>
            <li>• Pièce silencieuse, sans écho ni bruit de fond</li>
            <li>• Micro décent (AirPods propres, casque USB, micro externe)</li>
            <li>• Variez : affirmations, questions, exclamations</li>
            <li>• Astuce : extrayez l'audio directement de votre vidéo HeyGen — une seule prise !</li>
          </>
        )}
      </ul>
    </div>
  );
}


function MyAvatarProfileSection({ myProfile, onSaved }) {
  const [form, setForm] = useState({
    heygen_api_key: "", heygen_avatar_id: "", heygen_voice_id: "",
    elevenlabs_api_key: "", elevenlabs_voice_id: "",
  });
  const [saving, setSaving] = useState(false);

  // Préremplit les IDs (les clés restent vides — patch partiel côté backend)
  useEffect(() => {
    if (!myProfile) return;
    setForm({
      heygen_api_key: "",
      heygen_avatar_id: myProfile.heygen_avatar_id || "",
      heygen_voice_id: myProfile.heygen_voice_id || "",
      elevenlabs_api_key: "",
      elevenlabs_voice_id: myProfile.elevenlabs_voice_id || "",
    });
  }, [myProfile]);

  const save = async () => {
    setSaving(true);
    try {
      // N'envoie que les champs non vides pour préserver les clés existantes
      const body = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v && v.trim()));
      await api.put("/growth/avatar/my-profile", body);
      toast.success("Profil personnel enregistré.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec sauvegarde");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer votre profil personnel ?")) return;
    try {
      await api.delete("/growth/avatar/my-profile");
      toast.success("Profil supprimé.");
      onSaved();
    } catch { toast.error("Échec suppression"); }
  };

  const mineHgOk = myProfile?.heygen_api_key_present && myProfile?.heygen_avatar_id;
  const mineElOk = myProfile?.elevenlabs_api_key_present && myProfile?.elevenlabs_voice_id;

  return (
    <Card className="mt-4" testid="my-profile-section">
      <h3 className="font-semibold text-lg mb-3" style={{ color: INK }}>
        <span className="inline-block px-2 mr-2 rounded-full text-xs"
                style={{ background: CHAMPAGNE, color: NAVY }}>3</span>
        Mon profil personnel (multi-tenant)
      </h3>
      <p className="text-sm mb-4" style={{ color: SLATE }}>
        Chaque conseiller ALTIORA peut configurer son <strong>propre clone</strong> —
        avatar HeyGen et voix ElevenLabs distincts. Vos clés API sont
        <strong> chiffrées</strong> (Fernet AES-128) avant stockage et ne sont
        jamais renvoyées en clair. Laissez un champ clé <em>vide</em> pour
        conserver la valeur existante.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-4 rounded" style={{ background: "#F7F1E1" }}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold flex items-center gap-2"
                 style={{ color: NAVY }}>
              <Video size={14} /> HeyGen
            </h4>
            {mineHgOk ? (
              <span className="text-xs" style={{ color: "#0a7f42" }}>✔ Configuré</span>
            ) : (
              <span className="text-xs" style={{ color: SLATE }}>Non configuré</span>
            )}
          </div>
          <FormField label="Clé API HeyGen (secret)">
            <Input type="password" value={form.heygen_api_key}
                    onChange={e => setForm({ ...form, heygen_api_key: e.target.value })}
                    data-testid="my-heygen-key"
                    placeholder="hg_xxxxxxxxxxxx" />
          </FormField>
          <FormField label="Avatar ID">
            <Input value={form.heygen_avatar_id}
                    onChange={e => setForm({ ...form, heygen_avatar_id: e.target.value })}
                    data-testid="my-heygen-avatar"
                    placeholder="avatar_xxxxxxxxx" />
          </FormField>
          <FormField label="Voice ID HeyGen (optionnel)">
            <Input value={form.heygen_voice_id}
                    onChange={e => setForm({ ...form, heygen_voice_id: e.target.value })}
                    data-testid="my-heygen-voice"
                    placeholder="voice_xxxxxxxxx — laisser vide si ElevenLabs" />
          </FormField>
        </div>

        <div className="p-4 rounded" style={{ background: "#F7F1E1" }}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold flex items-center gap-2"
                 style={{ color: NAVY }}>
              <Mic size={14} /> ElevenLabs
            </h4>
            {mineElOk ? (
              <span className="text-xs" style={{ color: "#0a7f42" }}>✔ Configuré</span>
            ) : (
              <span className="text-xs" style={{ color: SLATE }}>Non configuré</span>
            )}
          </div>
          <FormField label="Clé API ElevenLabs (secret)">
            <Input type="password" value={form.elevenlabs_api_key}
                    onChange={e => setForm({ ...form, elevenlabs_api_key: e.target.value })}
                    data-testid="my-elevenlabs-key"
                    placeholder="xi-api-xxxxxxxxxxxx" />
          </FormField>
          <FormField label="Voice ID ElevenLabs">
            <Input value={form.elevenlabs_voice_id}
                    onChange={e => setForm({ ...form, elevenlabs_voice_id: e.target.value })}
                    data-testid="my-elevenlabs-voice"
                    placeholder="21m00Tcm4TlvDq8ikWAM" />
          </FormField>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <PrimaryButton onClick={save} disabled={saving}
                          data-testid="my-profile-save">
          {saving ? <Loader /> : "Enregistrer mon profil"}
        </PrimaryButton>
        {(mineHgOk || mineElOk) && (
          <GhostButton onClick={del} data-testid="my-profile-delete">
            Supprimer
          </GhostButton>
        )}
      </div>
    </Card>
  );
}

